import { useEffect, useState } from 'react';

import { Box, Center, Flex, Pagination, Select, Table, Text, Title } from '@mantine/core';

import { useGetNpiDataQuery } from '@/store/slices/api/npiApiSlice.ts';
import withErrorBoundary from '@/utils/withErrorBoundary.tsx';
import FallbackText from '@/components/FallbackText.tsx';
import SkeletonTable from '@/components/SkeletonTable';
import SortableHeader from '@/components/SortableHeader';

import { RegisteredNpi } from '@/types/npi.ts';

import classes from '../PatientEditDetailsModal.module.scss';

import type { SearchReferringPhysicianModalType as SearchParams } from './ReferringProviderSelectionModal.tsx';

type FilterType = {
  limit: string;
  offset: string;
  sortBy: string;
  sortDirection: string;
};

// Constants
const ROWS_PER_PAGE_OPTIONS = ['20', '50', '80'];

const TABLE_HEADERS = [
  { key: 'NPI', label: 'NPI', isSortable: true },
  { key: 'firstName', label: 'Name', isSortable: true },
  { key: 'location', label: 'Location', isSortable: false },
  { key: 'phone', label: 'Phone', isSortable: false },
  { key: 'taxonomyCode', label: 'Primary Taxonomy', isSortable: true }
];

const ReferringProviderTable = ({
  searchParams,
  handleUpdateReferringProvider
}: {
  searchParams: SearchParams | null;
  handleUpdateReferringProvider: (provider: RegisteredNpi) => void;
}) => {
  const [filter, setFilter] = useState<FilterType>({
    limit: '50',
    offset: '0',
    sortBy: '',
    sortDirection: 'DESC'
  });
  const [currentPage, setCurrentPage] = useState<number>(1);

  const {
    isLoading,
    isFetching,
    data: npiData
  } = useGetNpiDataQuery(
    {
      city: searchParams?.city?.trim() ?? '',
      state: searchParams?.state?.trim() ?? '',
      first_name: searchParams?.providerFirstName?.trim() ?? '',
      last_name: searchParams?.providerLastName?.trim() ?? '',
      npi: searchParams?.npiNumber?.toString()?.trim() ?? '',
      taxonomy: searchParams?.taxonomy ?? '',
      limit: filter.limit,
      offset: filter.offset,
      sortBy: filter.sortBy ?? '',
      sortDirection: filter.sortDirection ?? ''
    },
    { skip: !searchParams }
  );

  // Reset pagination when search parameters change
  useEffect(() => {
    setCurrentPage(1);
    setFilter((prev) => ({
      ...prev,
      offset: '0'
    }));
  }, [searchParams]);

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilter((prev) => ({
      ...prev,
      offset: ((page - 1) * parseInt(prev.limit)).toString()
    }));
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (value: string) => {
    setFilter((prev) => ({
      ...prev,
      limit: value,
      offset: '0' // Reset to the first page when changing rows per page
    }));
    setCurrentPage(1); // Reset pagination to the first page
  };

  // Handle sorting logic
  const handleSort = (column: string) => {
    setFilter((prev) => ({
      ...prev,
      sortBy: column,
      sortDirection: prev.sortBy === column && prev.sortDirection === 'ASC' ? 'DESC' : 'ASC'
    }));
  };

  // Don't display the component until the user clicks on search.
  if (!searchParams) return <></>;

  if (!isFetching && !isLoading && npiData?.data?.length === 0) {
    return (
      <Center h={'15rem'}>
        <Box>
          <Title order={4} c="gray.7" ta="center">
            No Records Found
          </Title>
          <Text size="sm" c="gray.6" ta="center" mt="xs">
            Try using different search filters to find the results you are looking for.
          </Text>
        </Box>
      </Center>
    );
  }

  return (
    <Flex direction={'column'} gap={2}>
      <Title size={'h5'} c={'gray.8'}>
        Results
      </Title>
      <Box bd={'1px solid gray.4'} mt={'4px'} className={classes.npiTableContainer}>
        {isLoading || isFetching ? (
          <SkeletonTable headers={TABLE_HEADERS} rows={10} />
        ) : (
          <Table stickyHeader>
            <Table.Thead>
              <Table.Tr>
                {TABLE_HEADERS.map((header) => (
                  <SortableHeader
                    key={header.key}
                    column={header.key}
                    label={header.label}
                    sortBy={filter.sortBy}
                    sortDirection={filter.sortDirection}
                    onSort={handleSort}
                    isSortable={header.isSortable}
                  />
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {npiData?.data?.map((item) => (
                <Table.Tr
                  key={item.NPI}
                  className={classes.npiTableRow}
                  onClick={() => handleUpdateReferringProvider(item)}>
                  <Table.Td py={'xs'}>
                    <FallbackText size={'sm'} c={'neutral.8'}>
                      {item.NPI}
                    </FallbackText>
                  </Table.Td>
                  <Table.Td>
                    <FallbackText size={'sm'} c={'neutral.8'}>
                      {item?.firstName} {item?.lastName}
                    </FallbackText>
                  </Table.Td>
                  <Table.Td>
                    <FallbackText size={'sm'} c={'neutral.8'}>
                      {item?.city} {item?.state} - {item?.zipCode}
                    </FallbackText>
                  </Table.Td>
                  <Table.Td>
                    <FallbackText size={'sm'} c={'neutral.8'}>
                      {item?.phoneNumber}
                    </FallbackText>
                  </Table.Td>
                  <Table.Td>
                    <FallbackText size={'sm'} c={'neutral.8'}>
                      {item?.taxonomyCode?.display_name}
                    </FallbackText>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Box>

      {npiData && npiData?.total > 0 && !isFetching && (
        <Flex justify={'flex-end'} gap={'sm'} align={'center'} mt={'md'}>
          <Flex gap={'xs'} align={'center'}>
            <Text size="sm">Rows per page:</Text>
            <Select
              data={ROWS_PER_PAGE_OPTIONS}
              value={filter.limit}
              onChange={(value) => {
                handleRowsPerPageChange(value as string);
              }}
              size="xs"
              w={'5rem'}
            />
          </Flex>
          <Pagination
            total={Math.ceil(npiData.total / parseInt(filter.limit))}
            value={currentPage}
            onChange={handlePageChange}
          />
        </Flex>
      )}
    </Flex>
  );
};

const ReferringPhysicianTableWithBoundary = withErrorBoundary(ReferringProviderTable, {
  fallback: <></>
});

export default ReferringPhysicianTableWithBoundary;
